import React, { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Divider, MenuItem, Popover, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowIcon,
  CenterFocusIcon,
  ClockIcon,
  ExternalLinkIcon,
  FloorRateIcon,
  FullScreenExitIcon,
  PrintIcon,
  RotateLeftIcon,
  RotateRightIcon,
  SaveIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { SCALES } from 'constants/documents';
import { KEY_CODES } from 'constants/keyboard';
import { PAGE_CODES } from 'constants/pageCodes';
import usePopover from 'hooks/usePopover';
import usePrevious from 'hooks/usePrevious';
import { checkIsGenAi } from 'utils/documents';
import { getPressedKey } from 'utils/keyboard';
import { checkIsDarkTheme } from 'utils/theme';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { getDocumentById } from 'store/documents/selectors';
import { openDialogAction } from 'store/global/actions';

import { DocumentTestIds } from '../../document.testIds';
import AiLayersPopover from '../AiLayersPopover';
import HistoryTable from '../HistoryModal/HistoryTable';

import { SCALE_TO_PERCENT } from './ControlPanel.constants';

const pageInputWrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  margin: '0 10px',
};

const selectedPageInputSx = { maxWidth: 54 };
const scaleDropdownSx = {
  width: 85,
  margin: '0 10px',

  '& .MuiSelect-select': {
    textOverflow: 'clip !important',
  },
};

const pageControls = [
  {
    icon: ArrowIcon,
    iconDirection: IconDirection.LEFT,
    testName: 'prev-page',
    isIncreasing: false,
    title: 'Previous page',
  },
  {
    icon: ArrowIcon,
    iconDirection: IconDirection.RIGHT,
    testName: 'next-page',
    isIncreasing: true,
    title: 'Next page',
  },
];

const rotationControls = [
  {
    icon: RotateLeftIcon,
    testName: 'rotate-left',
    isIncreasing: false,
    title: 'Rotate left',
  },
  { icon: RotateRightIcon, testName: 'rotate-right', isIncreasing: true, title: 'Rotate right' },
  { icon: SaveIcon, testName: 'save', title: 'Save' },
];

const scaleControls = [
  {
    icon: ZoomOutIcon,
    testName: 'zoom-out',
    isIncreasing: false,
    title: 'Zoom out',
  },
  { icon: ZoomInIcon, testName: 'zoom-in', isIncreasing: true, title: 'Zoom in' },
];

const selectInputProps = { 'aria-label': 'scale' };

const buttonTooltipPopperProps = { disablePortal: true };

const aiLayersPopoverOptions = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

const ControlPanel = ({
  docId,
  scale = 1,
  selectedPage = 1,
  pagesNumber = 1,
  isSavePageButtonDisabled = false,
  isPageSaving = false,
  isNavigateDisabled = false,
  onSelectScale,
  onSelectPage,
  onPrevPage,
  onNextPage,
  onRotateRight,
  onRotateLeft,
  onZoomIn,
  onZoomOut,
  onSavePage,
  onDownloadDocument,
  onPrintDocument,
  onOpenDocumentInNewTab,
  isDownloadable = false,
  isControlVisible = false,
  isPrintable = false,
  isOpenInNewTabAllowed = false,
  isViewHistoryAvailable = false,
  isRestrictedFunctionality = false,
  checkedAiLayers,
  onSelectAiLayer,
  isFocusMode = false,
  onToggleFocus = null,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDarkTheme = checkIsDarkTheme(theme.palette.type);

  const controlsWrapperSx = useMemo(
    () => ({
      padding: '10px 20px 0',
      borderRadius: '10px 10px 0 0',
      backgroundColor: isDarkTheme ? theme.palette.grey['700'] : theme.palette.grey['400'],
    }),
    [isDarkTheme, theme],
  );

  const controlSx = useMemo(
    () => ({
      '&:hover': {
        backgroundColor: theme.palette.grey['700'],
      },
      '&:not(:last-child)': {
        marginRight: '10px',
      },
    }),
    [theme],
  );

  const prevSelectedPage = usePrevious(selectedPage);
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();
  const [currentPage, setCurrentPage] = useState(selectedPage);

  const isDownloadAndPrintAllowed = useSelector(
    selectIsClaimAllowed(PAGE_CODES.DOWNLOAD_AND_PRINT_DOCUMENT),
  );
  const document = useSelector(getDocumentById(docId));

  const isGenAI = checkIsGenAi(document);

  useEffect(() => {
    if (selectedPage === prevSelectedPage) {
      return;
    }

    setCurrentPage(selectedPage);
  }, [selectedPage, prevSelectedPage]);

  const onChangeCurrentPage = e => {
    const { value } = e.target;

    if (/^\d+$/.test(Number(value))) {
      setCurrentPage(Number(value));
    }
  };

  const onSelectNextPage = e => {
    if (getPressedKey(e) === KEY_CODES.ENTER) {
      if (currentPage >= 1 && currentPage <= pagesNumber) {
        onSelectPage(currentPage);

        return;
      }

      setCurrentPage(selectedPage);
    }
  };

  const renderControlButton = ({ icon, iconDirection, handler, testName, disabled, title }) => (
    <ButtonWithTooltip
      PopperProps={buttonTooltipPopperProps}
      data-testid={testName}
      disabled={disabled}
      icon={icon}
      iconDirection={iconDirection}
      iconSize={IconSize[24]}
      key={testName}
      size="small"
      sx={controlSx}
      title={title}
      variant="outlined"
      onClick={handler}
    />
  );

  const renderSelectedPageInput = () => (
    <div style={pageInputWrapperStyle}>
      <TextFieldCDS
        data-testid={DocumentTestIds.SELECT_PAGE_INPUT}
        disabled={pagesNumber === 1 || isNavigateDisabled}
        name="selectedPage"
        sx={selectedPageInputSx}
        value={currentPage}
        onChange={onChangeCurrentPage}
        onKeyDown={onSelectNextPage}
      />
      <span className="font-bold px-8">of {pagesNumber || 1}</span>
    </div>
  );

  const renderScaleDropdown = () => (
    <Select
      displayEmpty
      data-testid={DocumentTestIds.SCALE_DROPDOWN_SELECT}
      inputProps={selectInputProps}
      sx={scaleDropdownSx}
      value={scale}
      onChange={onSelectScale}
    >
      {SCALES.map(scale => (
        <MenuItem
          data-testid={`scale.${scale * SCALE_TO_PERCENT}`}
          key={scale}
          value={scale}
        >{`${_.round(scale * SCALE_TO_PERCENT, 1)}%`}</MenuItem>
      ))}
    </Select>
  );

  const onOpenHistory = () => {
    dispatch(
      openDialogAction({
        header: 'Documents history',
        body: () => <HistoryTable docId={docId} />,
      }),
    );
  };

  return (
    <Box
      className="flex items-center full-width"
      data-testid={DocumentTestIds.DOCUMENT_PREVIEW_CONTROL_PANEL}
      sx={controlsWrapperSx}
    >
      <div className="flex p-8">
        {pageControls.map(({ icon, iconDirection, isIncreasing, testName, title }) =>
          renderControlButton({
            icon,
            iconDirection,
            testName,
            title,
            handler: isIncreasing ? onNextPage : onPrevPage,
            disabled:
              pagesNumber < 2 ||
              (isIncreasing && selectedPage === pagesNumber) ||
              (!isIncreasing && selectedPage === 1) ||
              isNavigateDisabled,
          }),
        )}
      </div>
      {renderSelectedPageInput()}

      {!isRestrictedFunctionality && (
        <>
          <Divider flexItem orientation="vertical" />
          <div className="flex p-8">
            {rotationControls.reduce((acc, { icon, isIncreasing, testName, title }) => {
              if (!isControlVisible && testName === 'save') {
                return acc;
              }

              acc.push(
                renderControlButton({
                  icon,
                  testName,
                  title,
                  disabled: _.isNil(isIncreasing) ? isSavePageButtonDisabled : isPageSaving,
                  handler:
                    (_.isNil(isIncreasing) && onSavePage) ||
                    (isIncreasing && onRotateRight) ||
                    (!isIncreasing && onRotateLeft),
                }),
              );

              return acc;
            }, [])}
          </div>
        </>
      )}

      <Divider flexItem orientation="vertical" />
      <div className="flex p-8">
        {scaleControls.map(({ icon, isIncreasing, testName, title }) =>
          renderControlButton({
            icon,
            testName,
            title,
            handler: isIncreasing ? onZoomIn : onZoomOut,
            disabled: isIncreasing ? scale === _.last(SCALES) : scale === _.first(SCALES),
          }),
        )}
      </div>
      {renderScaleDropdown()}

      {!isRestrictedFunctionality && (isControlVisible || isViewHistoryAvailable) && (
        <>
          <Divider flexItem orientation="vertical" />
          <div className="flex p-8">
            {renderControlButton({
              icon: ClockIcon,
              testName: 'document-preview__history',
              disabled: false,
              title: 'Show history',
              handler: onOpenHistory,
            })}
          </div>
        </>
      )}

      {isDownloadAndPrintAllowed && (
        <>
          {(isDownloadable || isPrintable) && <Divider flexItem orientation="vertical" />}

          {isDownloadable && (
            <div className="flex p-8">
              {renderControlButton({
                icon: FloorRateIcon,
                testName: 'document-download',
                disabled: isPageSaving,
                title: 'Download document',
                handler: onDownloadDocument,
              })}
            </div>
          )}

          {isPrintable && (
            <div className="flex p-8">
              {renderControlButton({
                icon: PrintIcon,
                testName: 'print-document-button',
                disabled: isPageSaving,
                title: 'Print document',
                handler: onPrintDocument,
              })}
            </div>
          )}
        </>
      )}
      {isOpenInNewTabAllowed && (
        <div className="flex p-8">
          {renderControlButton({
            icon: ExternalLinkIcon,
            testName: 'document-open-in-new-tab',
            title: 'Open in Separate Tab',
            handler: onOpenDocumentInNewTab,
          })}
        </div>
      )}

      {onToggleFocus && (
        <>
          <Divider flexItem orientation="vertical" />
          <div className="flex p-8">
            {renderControlButton({
              icon: isFocusMode ? FullScreenExitIcon : CenterFocusIcon,
              testName: 'document-focus-mode',
              title: isFocusMode ? 'Exit focus' : 'Focus',
              handler: onToggleFocus,
            })}
          </div>
        </>
      )}

      {isGenAI && checkedAiLayers && (
        <Button
          data-testid={DocumentTestIds.CONTROL_PANEL_LAYERS_BUTTON}
          size="large"
          type="button"
          onClick={onSetAnchorElement}
        >
          Layers
        </Button>
      )}

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={aiLayersPopoverOptions.anchorOrigin}
        open={!!anchorEl}
        transformOrigin={aiLayersPopoverOptions.transformOrigin}
        onClose={onClearAnchorElement}
      >
        <AiLayersPopover checkedAiLayers={checkedAiLayers} onSelectAiLayer={onSelectAiLayer} />
      </Popover>
    </Box>
  );
};

ControlPanel.propTypes = {
  onNextPage: PropTypes.func.isRequired,
  onPrevPage: PropTypes.func.isRequired,
  onSelectPage: PropTypes.func.isRequired,
  onSelectScale: PropTypes.func.isRequired,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,

  checkedAiLayers: PropTypes.object,
  docId: PropTypes.string,
  isControlVisible: PropTypes.bool,
  isDownloadable: PropTypes.bool,
  isFocusMode: PropTypes.bool,
  isNavigateDisabled: PropTypes.bool,
  isOpenInNewTabAllowed: PropTypes.bool,
  isPageSaving: PropTypes.bool,
  isPrintable: PropTypes.bool,
  isRestrictedFunctionality: PropTypes.bool,
  isSavePageButtonDisabled: PropTypes.bool,
  isViewHistoryAvailable: PropTypes.bool,
  pagesNumber: PropTypes.number,
  scale: PropTypes.number,
  selectedPage: PropTypes.number,
  onSelectAiLayer: PropTypes.func,
  onDownloadDocument: PropTypes.func,
  onOpenDocumentInNewTab: PropTypes.func,
  onPrintDocument: PropTypes.func,
  onRotateLeft: PropTypes.func,
  onRotateRight: PropTypes.func,
  onSavePage: PropTypes.func,
  onToggleFocus: PropTypes.func,
};

export default memo(ControlPanel);
